import { useState, useEffect } from 'react';
import { AggregatedDataService } from '../services/AggregatedDataService';

/**
 * Fetches readings for ALL sensors in a zone and merges them into a single
 * "zone average" time series. Uses the exact same time-range vocabulary as
 * DeviceDetailPage / useRedisReadings ('1h' | '6h' | '24h' | '7d' | '30d') so
 * the time filter at the top of the page drives the correct data window.
 *
 * Each sensor's readings are fetched via AggregatedDataService (Redis cache
 * first, aggregated Supabase tables second). Readings from every sensor are
 * then collapsed into fixed-width time buckets and averaged, producing one
 * series suitable for the single-line SensorChart.
 *
 * Returns the same shape as useRedisReadings so ZoneDetailPage mirrors
 * DeviceDetailPage: { readings, downsampledReadings, isLoading, isSyncing }.
 */
const TIME_RANGE_MS = {
  '1h': 1 * 60 * 60 * 1000,
  '6h': 6 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

const TIME_RANGE_PERIOD = {
  '1h': 'HOUR',
  '6h': 'HOUR',
  '24h': 'DAY',
  '7d': 'WEEK',
  '30d': 'MONTH',
};

// Width of each merge bucket (ms). Readings from different sensors rarely
// share an exact timestamp, so we round each into one of these buckets and
// average. Granularity loosens with the time range, matching AggregatedDataService.
const BUCKET_MS = {
  '1h': 5 * 60 * 1000,          // 5 min
  '6h': 15 * 60 * 1000,         // 15 min
  '24h': 30 * 60 * 1000,        // 30 min
  '7d': 2 * 60 * 60 * 1000,     // 2 hours
  '30d': 6 * 60 * 60 * 1000,    // 6 hours
};

export const useZoneReadings = (sensorIds, timeRange = '24h') => {
  const [readings, setReadings] = useState([]);
  const [downsampledReadings, setDownsampledReadings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);

  // Stable key for the effect dependency (array identity changes each render).
  const sensorIdsKey = (sensorIds || []).join(',');

  useEffect(() => {
    const ids = sensorIdsKey ? sensorIdsKey.split(',') : [];
    if (ids.length === 0) {
      setReadings([]);
      setDownsampledReadings([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const rangeMs = TIME_RANGE_MS[timeRange] || TIME_RANGE_MS['24h'];
    const period = TIME_RANGE_PERIOD[timeRange] || 'DAY';
    const bucketMs = BUCKET_MS[timeRange] || BUCKET_MS['24h'];
    const endDate = new Date();
    const startDate = new Date(Date.now() - rangeMs);

    const load = async () => {
      setIsLoading(true);
      setIsSyncing(true);
      try {
        // Fetch each sensor's aggregated series in parallel.
        const results = await Promise.all(
          ids.map((id) =>
            AggregatedDataService.fetchChartData(id, period, startDate, endDate)
              .catch((e) => {
                console.error(`useZoneReadings: fetch failed for ${id}:`, e);
                return { readings: [] };
              })
          )
        );
        if (cancelled) return;

        // Bucket-merge across sensors.
        const buckets = new Map(); // bucketStartMs -> { temps: [], humidities: [] }
        results.forEach((res) => {
          (res?.readings || []).forEach((r) => {
            const ts = r.timestamp instanceof Date ? r.timestamp : new Date(r.timestamp);
            if (isNaN(ts.getTime())) return;
            const key = Math.floor(ts.getTime() / bucketMs) * bucketMs;
            if (!buckets.has(key)) buckets.set(key, { temps: [], humidities: [] });
            const b = buckets.get(key);
            if (r.temperature != null && !Number.isNaN(r.temperature)) b.temps.push(r.temperature);
            if (r.humidity != null && !Number.isNaN(r.humidity) && r.humidity > 0) b.humidities.push(r.humidity);
          });
        });

        const merged = [...buckets.entries()]
          .sort((a, b) => a[0] - b[0])
          .map(([key, b]) => {
            const iso = new Date(key).toISOString();
            return {
              temperature: b.temps.length ? b.temps.reduce((s, v) => s + v, 0) / b.temps.length : null,
              humidity: b.humidities.length ? b.humidities.reduce((s, v) => s + v, 0) / b.humidities.length : null,
              recorded_at: iso,
              created_at: iso,
              timestamp: new Date(key),
            };
          });

        setReadings(merged);
        setDownsampledReadings(merged); // already bucketed to chart-friendly density
      } catch (e) {
        console.error('useZoneReadings error:', e);
        setReadings([]);
        setDownsampledReadings([]);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsSyncing(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensorIdsKey, timeRange]);

  return { readings, downsampledReadings, isLoading, isSyncing };
};
